import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ExternalLink, MessagesSquare } from 'lucide-react';
import { allPublicationsApi, type AllPublicationItem, type AllPublicationInsights } from '../../shared/api/all-publications.api';
import { toast } from 'sonner';
import { readStashedFeedItem } from './feedItemStash';
import { commentsLink } from '../publications/lib/journal';
import { ROUTES } from '../../shared/config/routes';
import { Skeleton } from '../../components/ui/skeleton';

export function AllPublicationDetailPage() {
  const { mediaId } = useParams<{ mediaId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Пост приходит из «Всей ленты»: в state перехода, а после перезагрузки —
  // из сессии. Отдельной ручки «пост по mediaId» у ленты нет.
  const item = (location.state as { item?: AllPublicationItem } | null)?.item ?? readStashedFeedItem(mediaId);

  const [insights, setInsights] = useState<AllPublicationInsights | null>(null);
  const [refreshing, setRefreshing] = useState(true);

  useEffect(() => {
    if (!item) return;
    let mounted = true;

    const load = async () => {
      try {
        setRefreshing(true);
        const map = await allPublicationsApi.getInsights([
          {
            mediaId: item.mediaId,
            accountId: item.accountId,
            mediaType: item.mediaType,
            likes: item.likes,
            comments: item.comments,
          },
        ]);
        if (mounted) setInsights(map[item.mediaId] ?? null);
      } catch (error) {
        console.error('Error loading publication insights:', error);
        if (mounted) toast.error('Ошибка загрузки статистики');
      } finally {
        if (mounted) setRefreshing(false);
      }
    };

    load();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaId]);

  // Прямой заход по ссылке (не из ленты этой вкладки) — данных поста нет
  if (!item) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-2 text-center">
        <p className="font-medium">Карточку этого поста по прямой ссылке не открыть</p>
        <p className="max-w-md text-sm text-muted-foreground">
          Она открывается из «Всей ленты»: найдите пост там — статистика подтянется сама.
          Посты, отправленные через InstaFarm, открываются по ссылке из журнала «Через InstaFarm».
        </p>
        <div className="mt-2 flex flex-wrap justify-center gap-2">
          <Button onClick={() => navigate(`${ROUTES.PUBLICATIONS}/feed`)}>Открыть «Всю ленту»</Button>
          <Button variant="outline" onClick={() => navigate(ROUTES.PUBLICATIONS)}>Журнал «Через InstaFarm»</Button>
        </div>
      </div>
    );
  }
  const comments = commentsLink([item.permalink]);

  const calculateER = () => {
    if (!insights) return null;
    return insights.er > 0 ? insights.er.toFixed(2) + '%' : null;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Детали публикации</CardTitle>
            <CardDescription>
              {refreshing ? 'Обновление статистики...' : 'Статистика по публикации'}
            </CardDescription>
          </div>
          {comments && (
            <Button variant="outline" size="sm" onClick={() => navigate(comments)}>
              <MessagesSquare className="size-4" />
              Разобрать комментарии
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {refreshing && !insights ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Аккаунт</TableHead>
                  <TableHead className="text-right">ER</TableHead>
                  <TableHead className="text-right">Просмотры</TableHead>
                  <TableHead className="text-right">Лайки</TableHead>
                  <TableHead className="text-right">Комментарии</TableHead>
                  <TableHead className="text-right">Репосты</TableHead>
                  <TableHead className="text-right">Сохранения</TableHead>
                  <TableHead className="text-right">Охват</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <div className="font-medium">{item.username}</div>
                  </TableCell>
                  <TableCell className="text-right">{calculateER() || '—'}</TableCell>
                  <TableCell className="text-right">{insights?.views?.toLocaleString() || '—'}</TableCell>
                  <TableCell className="text-right">{item.likes.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{item.comments.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{insights?.shares?.toLocaleString() || '—'}</TableCell>
                  <TableCell className="text-right">{insights?.saved?.toLocaleString() || '—'}</TableCell>
                  <TableCell className="text-right">{insights?.reach?.toLocaleString() || '—'}</TableCell>
                  <TableCell>
                    <Button asChild variant="ghost" className="h-8 w-8 p-0">
                      <a href={item.permalink} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
